import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { ReportId, ReportingComparisonScope } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { hasPermissionGuard } from 'ag-common-svc/shared/utils/has-permission-guard';
import {
  REPORTS_VIEWER_SECTIONS,
  getReportsViewerTileEntity,
  type ReportsViewerSection,
  type ReportsViewerTile,
} from '../../utils/report-sections.utils';
import { PRODUCTION_DASHBOARD_CONTEXT } from '../production-dashboards';
import { ReportDefaultSettingsModalComponent } from './report-default-settings-modal/report-default-settings-modal.component';
import {
  REPORTS_VIEWER_COMPARISON_ROUTE_PATH,
  REPORTS_VIEWER_PRODUCTION_HISTORY_ROUTE_PATH,
  toReportRouteId,
} from './reports-viewer-routing.constants';

/**
 * The report tiles page, shared by ARM and the portal.
 *
 * Shows the sections the app says this viewer sees, and inside them only the tiles whose
 * entity the viewer holds. Every tile opens a routed page that carries the section's scope.
 */
@Component({
  selector: 'ag-shr-reports-viewer',
  templateUrl: './reports-viewer.component.html',
  styleUrls: ['./reports-viewer.component.scss'],
  host: {
    class: 'my-reports',
  },
})
export class ReportsViewerComponent {
  @ViewChild('reportDefaultSettingsModalRef', { static: true })
  private reportDefaultSettingsModalComponent?: ReportDefaultSettingsModalComponent;

  private readonly router = inject(Router);
  private readonly dashboardContext = inject(PRODUCTION_DASHBOARD_CONTEXT);
  private navigationRequestId = 0;

  protected readonly canManageDefaultsSig = this.dashboardContext.canManageDefaultsSig;
  protected readonly isOpeningReportSig = signal(false);

  /** The sections and tiles this viewer may open, in display order. Empty groups and sections drop out. */
  protected readonly sectionsSig = computed<ReportsViewerSection[]>(() => {
    const visibleSections = this.dashboardContext.visibleSectionsSig();
    const permissions = this.dashboardContext.permissionsSig() ?? {};
    const canOpen = (tile: ReportsViewerTile) => hasPermissionGuard([getReportsViewerTileEntity(tile)], permissions);

    return REPORTS_VIEWER_SECTIONS.filter(section => visibleSections.has(section.id))
      .map(section => ({
        ...section,
        groups: section.groups
          .map(group => ({ ...group, tiles: group.tiles.filter(canOpen) }))
          .filter(group => group.tiles.length),
      }))
      .filter(section => section.groups.length);
  });

  protected reportIdOf(tile: ReportsViewerTile): ReportId | null {
    return tile.kind === 'report' ? tile.reportId : null;
  }

  protected openTile(tile: ReportsViewerTile, section: ReportsViewerSection): void {
    if (tile.kind === 'comparison') {
      void this.router.navigate([
        ...this.dashboardContext.myReportsRouteCommands,
        REPORTS_VIEWER_COMPARISON_ROUTE_PATH,
        section.scope,
      ]);
      return;
    }

    if (tile.kind === 'productionTracking') {
      void this.router.navigate([
        ...this.dashboardContext.myReportsRouteCommands,
        REPORTS_VIEWER_PRODUCTION_HISTORY_ROUTE_PATH,
        section.scope,
      ]);
      return;
    }

    void this.openReport(tile.reportId, section.scope);
  }

  protected onSettingsClick(event: { event?: Event }, reportId: ReportId): void {
    event.event?.stopPropagation();
    void this.reportDefaultSettingsModalComponent?.showModal(reportId);
  }

  private async openReport(reportId: ReportId, scope: ReportingComparisonScope): Promise<void> {
    const requestId = ++this.navigationRequestId;
    this.isOpeningReportSig.set(true);

    try {
      if (requestId !== this.navigationRequestId) {
        return;
      }

      await this.router.navigate([...this.dashboardContext.myReportsRouteCommands, toReportRouteId(reportId)], {
        queryParams: {
          scope,
        },
      });
    } finally {
      if (requestId === this.navigationRequestId) {
        this.isOpeningReportSig.set(false);
      }
    }
  }
}
